import { createState, asNonNullSubscribable } from '../helpers/state-subject';
import { ensureGlobalState, logInstanceId } from '../helpers/state';
import { isRetryableHttpStatus, retryFetch } from '../helpers/retry-fetch.helper';
logInstanceId("cart.service");
const TIERED_PROGRESS_SECTION = 'tiered-progress-data';
function getTieredProgressSection() {
    return document.getElementById(TIERED_PROGRESS_SECTION) ? TIERED_PROGRESS_SECTION : undefined;
}
const SHARED_REQUEST_CONFIG = {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
};
export const cartState = ensureGlobalState("untuckit.cart", createState(null));
export const onCartUpdate = asNonNullSubscribable(cartState);
let cartMutationQueue = Promise.resolve();
function drainMutationQueue() {
    return undefined;
}
function enqueueCartMutation(fn) {
    const result = cartMutationQueue.then(fn, fn);
    cartMutationQueue = result.then(drainMutationQueue, drainMutationQueue);
    return result;
}
export class CartRequestError extends Error {
    status;
    retryable;
    responseBody;
    constructor(message, status, retryable, responseBody) {
        super(message);
        this.name = 'CartRequestError';
        this.status = status;
        this.retryable = retryable;
        this.responseBody = responseBody;
    }
}
export class ChangeLineItemError extends Error {
    payload;
    constructor(message, payload) {
        super(message);
        this.name = "ChangeLineItemError";
        this.payload = payload;
    }
}
function normalizeCartItems(cart) {
    cart.items = cart.items.filter(i => i.quantity > 0) || [];
    return cart;
}
function applyCartState(cart) {
    const normalized = normalizeCartItems(cart);
    cartState.set(normalized);
    return normalized;
}
async function mergeTieredProgressSections(cart) {
    const section = getTieredProgressSection();
    if (!section) {
        return cart;
    }
    const sectionsResponse = await retryFetch(`${window.Shopify.routes.root}?sections=${section}`);
    const sections = await readOptionalSections(sectionsResponse);
    if (sections) {
        cart.sections = sections;
        cartState.set(normalizeCartItems(cart));
    }
    return cart;
}
async function postCartRequest(path, body) {
    return retryFetch(`${window.Shopify.routes.root}${path}`, {
        ...SHARED_REQUEST_CONFIG,
        body: JSON.stringify(body)
    });
}
function throwCartRequestError(description, status, json, headers, requestPayload) {
    logErrors(description, json, headers, requestPayload);
    throw new CartRequestError(getResponseError(json), status, isRetryableHttpStatus(status), json);
}
/**
 * Shopify error responses (504, 502, and similar) are often an empty body or an HTML page.
 * `response.json()` throws SyntaxError before the status can be turned into a CartRequestError.
 */
async function readJsonBody(response) {
    const text = await response.text();
    if (!text.trim()) {
        return undefined;
    }
    try {
        return JSON.parse(text);
    }
    catch {
        return undefined;
    }
}
function isJsonObject(value) {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}
function toCartError(response, json) {
    if (!isJsonObject(json)) {
        return { status: response.status, message: '' };
    }
    return {
        status: typeof json.status === 'number' ? json.status : response.status,
        message: typeof json.message === 'string' ? json.message : '',
        description: typeof json.description === 'string' ? json.description : undefined,
    };
}
async function readCartPayload(response, description, requestPayload) {
    const json = await readJsonBody(response);
    if (!response.ok || !isJsonObject(json)) {
        throwCartRequestError(description, response.status, toCartError(response, json), response.headers, requestPayload);
    }
    return json;
}
async function readOptionalSections(response) {
    if (!response?.ok) {
        return undefined;
    }
    const json = await readJsonBody(response);
    return isJsonObject(json) ? json : undefined;
}
async function fetchCartData() {
    const section = getTieredProgressSection();
    const root = window.Shopify.routes.root;
    const [cartResponse, sectionsResponse] = await Promise.all([
        retryFetch(`${root}cart.js`),
        section ? retryFetch(`${root}?sections=${section}`) : Promise.resolve(null)
    ]);
    const json = await readCartPayload(cartResponse, "Error fetching cart", null);
    const cart = normalizeCartItems(json);
    const sections = await readOptionalSections(sectionsResponse);
    if (sections) {
        cart.sections = sections;
    }
    cartState.set(cart);
    return cart;
}
export async function updateCart(request) {
    return enqueueCartMutation(async () => {
        const response = await postCartRequest('cart/update.js', request);
        const json = await readCartPayload(response, "Error updating cart", request);
        return applyCartState(json);
    });
}
export async function addToCart(request) {
    return enqueueCartMutation(async () => {
        const response = await postCartRequest('cart/add.js', request);
        const json = await readCartPayload(response, "Error adding item to cart", request);
        await fetchCartData();
        return json;
    });
}
export async function getCart() {
    return fetchCartData();
}
export async function changeLineItem(request) {
    return enqueueCartMutation(async () => {
        const response = await postCartRequest('cart/change.js', request);
        const json = await readJsonBody(response);
        if (!response.ok) {
            const errorBody = toCartError(response, json);
            logErrors("Error changing line item", errorBody, response.headers, request);
            let reason = 'GENERIC';
            let passedthroughServerMessage = '';
            if (response.status === 422) {
                const lowercaseMessage = (errorBody.message || '').toLowerCase();
                // not great, but shopify doesn't provide error codes
                if (lowercaseMessage.includes('sold out')) {
                    reason = 'SOLD_OUT';
                }
                else if (lowercaseMessage.includes('due to availability')) {
                    reason = 'CANNOT_ADD_INVENTORY';
                    passedthroughServerMessage = errorBody.message;
                    await fetchCartData();
                }
            }
            throw new ChangeLineItemError('', { reason, passedthroughServerMessage });
        }
        if (!isJsonObject(json)) {
            throwCartRequestError("Error changing line item", response.status, toCartError(response, json), response.headers, request);
        }
        const cart = applyCartState(json);
        return mergeTieredProgressSections(cart);
    });
}
function getResponseError(json) {
    return json?.description || json?.message || 'unknown';
}
function logErrors(description, response, headers, requestPayload) {
    const responseError = getResponseError(response);
    console.error(`${description}:`, responseError);
    const requestId = headers.get('x-request-id');
    if (requestId) {
        console.error("cart request id", requestId, JSON.stringify(requestPayload));
    }
}
